/**
 * Sortable, collapsible table for resolved rows. Real table semantics.
 * Extra rows stay out of the accessibility tree until the disclosure opens.
 */
import { el } from '../../core/dom.js';
import { button } from '../button.js';
import { csvFilename, csvMatrix, downloadCsv } from './csv.js';
import { isUnverified, UNVERIFIED_TITLE } from './kpi-figure.js';
import {
  COLLAPSE_AFTER, askAboutRow, cellRaw, cellText, cellValue, rawColumnSlots,
} from './shape.js';

/**
 * @param {HTMLElement} node
 * @param {boolean} hidden
 */
function setHidden(node, hidden) {
  if (hidden) node.setAttribute('hidden', '');
  else node.removeAttribute('hidden');
}

function isNumeric(column) {
  return column.format === 'number' || column.format === 'percent' || column.format === 'currency' || column.align === 'right';
}

function isTextColumn(column) {
  return column.format === 'text' || column.wrap === true;
}

function widthStyle(column) {
  if (column.width === 'narrow') return 'width:14%';
  if (column.width === 'wide') return 'width:36%';
  if (column.width === 'normal') return 'width:22%';
  return '';
}

/**
 * @param {object} column
 * @param {unknown} cell
 * @returns {HTMLElement}
 */
function cellNode(column, cell) {
  const text = cellText(cell);
  const parts = ['chat-td'];
  if (isTextColumn(column) || column.wrap) parts.push('is-wrap');
  else parts.push('is-clip');
  if (isNumeric(column)) parts.push('is-num');
  if (column.emphasis) parts.push('is-em');
  const unverified = isUnverified(cell);
  if (unverified) parts.push('is-unverified');
  const tone = cell && typeof cell === 'object' ? cell.tone : '';
  if (tone) parts.push(`is-${tone}`);
  let style = widthStyle(column);
  if (cell && typeof cell === 'object' && typeof cell.heat === 'number') {
    const heat = Math.max(0, Math.min(100, Math.round(cell.heat * 100)));
    style = `${style};background:color-mix(in srgb, var(--signal) ${heat}%, transparent)`;
  }
  const tip = unverified ? UNVERIFIED_TITLE : (column.wrap || isTextColumn(column) ? null : text);
  const body = unverified
    ? el('span', { class: 'chat-unverified', title: UNVERIFIED_TITLE }, text)
    : text;
  return el('td', {
    class: parts.join(' '),
    style: style || null,
    title: tip,
  }, body);
}

function compareCells(column, dir, a, b) {
  const av = cellValue(a[column.key]);
  const bv = cellValue(b[column.key]);
  let cmp = 0;
  if (av != null && bv != null) cmp = av - bv;
  else cmp = cellText(a[column.key]).localeCompare(cellText(b[column.key]), undefined, { numeric: true });
  return dir === 'asc' ? cmp : -cmp;
}

function rowLabel(row, columns) {
  const key = columns[0] && columns[0].key;
  return cellText(key ? row[key] : '');
}

/**
 * @param {object} opts
 * @returns {HTMLElement}
 */
export function renderDataTable(opts) {
  const columns = Array.isArray(opts.columns) ? opts.columns : [];
  const allRows = Array.isArray(opts.rows) ? opts.rows.slice() : [];
  const title = opts.title || '';
  const titleId = `chat-table-title-${opts.id || Math.random().toString(36).slice(2, 8)}`;
  const bodyId = `chat-table-body-${opts.id || Math.random().toString(36).slice(2, 8)}`;
  let sortKey = '';
  let sortDir = 'asc';
  let open = false;
  const body = el('tbody', { id: bodyId });
  const headRow = el('tr');
  const table = el('table', { class: 'chat-table', 'aria-labelledby': title ? titleId : null },
    el('thead', {}, headRow), body);
  const more = el('button', { type: 'button', class: 'chat-more', 'aria-controls': bodyId });
  more.addEventListener('click', () => {
    open = !open;
    paint();
  });

  function ask(row) {
    if (!opts.actions || typeof opts.actions.onAsk !== 'function') return;
    opts.actions.onAsk(askAboutRow(opts.askTitle || title, rowLabel(row, columns)));
  }

  function headerCell(column) {
    const style = widthStyle(column) || null;
    const attrs = {
      scope: 'col',
      style,
      'data-col': column.key,
      class: isNumeric(column) ? 'is-num' : null,
    };
    if (!opts.sortable) return el('th', attrs, column.label || column.key);
    const control = el('button', {
      type: 'button',
      class: 'chat-sort',
      onClick: () => {
        if (sortKey === column.key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        else { sortKey = column.key; sortDir = 'asc'; }
        paint();
      },
    }, column.label || column.key);
    return el('th', { ...attrs, 'aria-sort': 'none' }, control);
  }

  function dataRow(row, hidden) {
    const label = rowLabel(row, columns);
    return el('tr', {
      class: row.tone ? `is-${row.tone}` : null,
      hidden: hidden || null,
      tabindex: '0',
      'aria-label': askAboutRow(opts.askTitle || title, label),
      onClick: () => ask(row),
      onKeydown: (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        ask(row);
      },
    }, columns.map((column) => cellNode(column, row[column.key])));
  }

  function totalsRow() {
    const totals = opts.totals;
    if (!totals) return null;
    const cells = totals.cells || {};
    return el('tr', { class: 'is-total' }, columns.map((column, index) => {
      if (index === 0 && cells[column.key] == null) return cellNode(column, totals.label || 'Total');
      return cellNode(column, cells[column.key]);
    }));
  }

  function paint() {
    headRow.replaceChildren(...columns.map(headerCell));
    const column = columns.find((item) => item.key === sortKey);
    const sorted = column ? allRows.slice().sort((a, b) => compareCells(column, sortDir, a, b)) : allRows.slice();
    const limit = open ? sorted.length : Math.min(sorted.length, COLLAPSE_AFTER);
    body.replaceChildren();
    sorted.forEach((row, index) => body.append(dataRow(row, index >= limit)));
    const totals = totalsRow();
    if (totals) body.append(totals);
    const extra = Math.max(0, allRows.length - COLLAPSE_AFTER);
    setHidden(more, extra === 0);
    more.setAttribute('aria-expanded', open ? 'true' : 'false');
    more.textContent = open ? 'Show fewer rows' : `Show ${extra} more rows`;
    columns.forEach((item) => {
      const th = [...headRow.children].find((node) => node.getAttribute('data-col') === item.key);
      if (!th || !opts.sortable) return;
      const pressed = sortKey === item.key;
      const name = pressed ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
      th.setAttribute('aria-sort', name);
      const control = th.querySelector('button');
      if (!control) return;
      const suffix = pressed ? `, ${name}` : '';
      control.setAttribute('aria-label', `Sort by ${item.label || item.key}${suffix}`);
    });
  }

  function onDownload() {
    const matrix = csvMatrix(columns, allRows);
    if (opts.totals) {
      const slots = rawColumnSlots(columns, allRows);
      const cells = opts.totals.cells || {};
      const line = [];
      columns.forEach((column, index) => {
        const text = cells[column.key] != null
          ? cellText(cells[column.key])
          : (index === 0 ? (opts.totals.label || 'Total') : '');
        line.push(text);
        if (slots[index]) line.push(cellRaw(cells[column.key]));
      });
      matrix.push(line);
    }
    downloadCsv(csvFilename(title || opts.type || 'table'), matrix);
  }

  paint();
  const download = opts.csv === false ? null : button('Download CSV', {
    className: 'chat-tool',
    onClick: onDownload,
  });
  if (download) download.setAttribute('aria-label', `Download ${title || 'table'} CSV`);
  const tools = el('div', { class: 'chat-block-tools' },
    title ? el('h3', { class: 'chat-block-title', id: titleId }, title) : el('span', { id: titleId, class: 'chat-sr' }, opts.type || 'Table'),
    download);
  const note = opts.note ? el('p', { class: 'chat-note' }, opts.note) : null;
  const truncated = opts.truncated
    ? el('p', { class: 'chat-note' }, opts.shown ? `Showing first ${opts.shown}` : 'Showing the first rows')
    : null;
  return el('section', {
    class: opts.nested ? 'chat-subtable' : `chat-block chat-block-${opts.type || 'table'}`,
    'data-block-type': opts.nested ? null : (opts.type || 'table'),
    'data-block-id': opts.nested ? null : (opts.id || ''),
  }, tools, el('div', { class: 'chat-scroll' }, table), more, note, truncated);
}
